import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, map, Observable, Subject, takeUntil } from 'rxjs';

import { TriggerAction } from '@shared/actions';
import { StateManagerProjectService } from '@state-manager';
import { CornerstoneService } from '@viewer/cornerstone.service';
import { BaseToolComponent } from '../base-tool/base-tool.component';
import { CornerstoneTool, ViewWindowIndex } from '@viewer/data';
import { ConfirmDialogService } from '@shared/confirm-dialog';
import {
  ConfirmDialogAnswer,
  ConfirmDialogResults,
} from '@shared/confirm-dialog/confirm-dialog.data';

@Component({
  selector: 'app-reset-changes-tool',
  templateUrl: 'reset-changes-tool.component.html',
  styleUrls: ['reset-changes-tool.component.scss'],
})
export class ResetChangesToolComponent
  extends BaseToolComponent
  implements OnInit, OnDestroy
{
  resetChangesToolAction!: TriggerAction;

  public resetChangesDisabled$: Observable<boolean>;
  private _unsubcribe$ = new Subject<void>();

  constructor(
    public stateManagerProjectService: StateManagerProjectService,
    private _confirmDialogService: ConfirmDialogService,
    private _cornerstoneService: CornerstoneService
  ) {
    super(stateManagerProjectService);

    this.resetChangesDisabled$ = combineLatest([
      this.stateManagerProjectService.isViewerLoaded$,
      this.stateManagerProjectService.isProcessOfResetDiff$,
      this.stateManagerProjectService.isProcessOfShowDiffHide$,
      this.stateManagerProjectService.isActiveShowChangesMode$,
      this.stateManagerProjectService.isDisabledAllToolsForChangeMask$,
      this.stateManagerProjectService.isMaskWasChanged$,
    ]).pipe(
      map(
        ([
          isViewerLoaded,
          isProcessOfResetDiff,
          isProcessOfShowDiffHide,
          isActiveShowChangesMode,
          isDisabledAllToolsForChangeMask,
          isMaskWasChanged,
        ]) =>
          !isViewerLoaded ||
          isProcessOfResetDiff[`viewer${ViewWindowIndex.Coronal}`] ||
          isProcessOfResetDiff[`viewer${ViewWindowIndex.Axial}`] ||
          isProcessOfResetDiff[`viewer${ViewWindowIndex.Sagittal}`] ||
          isProcessOfShowDiffHide[`viewer${ViewWindowIndex.Coronal}`] ||
          isProcessOfShowDiffHide[`viewer${ViewWindowIndex.Axial}`] ||
          isProcessOfShowDiffHide[`viewer${ViewWindowIndex.Sagittal}`] ||
          isActiveShowChangesMode ||
          isDisabledAllToolsForChangeMask ||
          !isMaskWasChanged
      )
    );
  }

  ngOnInit(): void {
    this.resetChangesToolAction = new TriggerAction(
      'reset-changes',
      'Reset Changes',
      (_) => {
        this._confirmDialogService
          .openConfirmDialog(
            'Dialog.RESET_CHANGES_MESSAGE',
            'Dialog.RejectButtons.CANCEL',
            'Dialog.ConfirmButtons.YES'
          )
          .pipe(takeUntil(this._unsubcribe$))
          .subscribe((res: ConfirmDialogAnswer) => {
            if (res.result === ConfirmDialogResults[1]) {
              this._cornerstoneService.activateResetDiff();
            }
          });
      }
    );

    this.resetChangesToolAction.isEnable = () =>
      this.resetChangesDisabled$.pipe(map((x) => !x));
  }

  ngOnDestroy(): void {
    this._unsubcribe$.next();
    this._unsubcribe$.complete();
  }
}
